import { FC } from 'react'
import { SweepstakeObject } from '@/types/codegen-federation'
import { SimpleTicketItem } from '@/views/TicketCheckoutViews/hooks/useSimpleTicketList'
import { ComingSoonReleases } from '@/views/TicketsLandingView/NewTicketsLandingView/components/ComingSoonReleases'
import { ClaimFreeTickets } from './components/ClaimFreeTickets'
import { CurrentReleases } from './components/CurrentReleases'
import { GuildRedemptionUpsell } from './components/GuildRedemptionUpsell'
import { GuildUpsell } from './components/GuildUpsell'
import { Hero } from './components/Hero'
import { PastReleases } from './components/PastReleases'
import { Sweepstakes } from './components/Sweepstakes'
import { UserMovieTickets } from './components/UserMovieTickets'
import { useGetFeaturedRelease, useGetHeroRelease, useGetReleasesWithGuildDiscounts } from './hooks'
import { ComingSoonTheatricalRelease, CurrentTheatricalRelease, PastTheatricalRelease } from './types'

const paddingBlendY = 'py-12 md:py-[5rem] xl:py-[6rem]'
const paddingNonBlendY = 'py-16 md:py-[6rem] xl:py-[12.5rem]'

interface Props {
  currentReleases: CurrentTheatricalRelease[]
  comingSoonReleases: ComingSoonTheatricalRelease[]
  pastReleases: PastTheatricalRelease[]
  userTickets: SimpleTicketItem[]
  sweepstakes: SweepstakeObject[]
  isBasicGuildUser: boolean
  isPremiumGuildUser: boolean
  isLoggedIn: boolean
}

export const NewTicketsLandingView: FC<Props> = ({
  currentReleases,
  pastReleases,
  comingSoonReleases,
  userTickets,
  sweepstakes,
  isBasicGuildUser,
  isPremiumGuildUser,
  isLoggedIn,
}) => {
  const { heroRelease, heroUserTickets } = useGetHeroRelease(currentReleases, userTickets)
  const { featuredRelease } = useGetFeaturedRelease(currentReleases)
  const { releasesWithGuildDiscounts } = useGetReleasesWithGuildDiscounts(currentReleases)
  const hasMovieTickets = userTickets.length > 0

  return (
    <main className="min-h-dvh overflow-hidden">
      <Hero release={heroRelease} userTickets={heroUserTickets} />

      {hasMovieTickets && <UserMovieTickets className={paddingBlendY} tickets={userTickets} />}

      <CurrentReleases
        releases={currentReleases}
        className={hasMovieTickets ? paddingBlendY : 'pb-[48px] pt-[64px] md:pb-[96px] md:pt-[120px]'}
        addAnimation={!!hasMovieTickets}
      />

      {comingSoonReleases.length > 0 && <ComingSoonReleases className={paddingBlendY} releases={comingSoonReleases} />}

      {!isPremiumGuildUser && <GuildUpsell className={paddingBlendY} isBasicGuildUser={isBasicGuildUser} />}

      {releasesWithGuildDiscounts.length > 0 && (
        <GuildRedemptionUpsell
          className={paddingBlendY}
          releases={releasesWithGuildDiscounts}
          comingSoonReleases={comingSoonReleases}
        />
      )}

      {!isLoggedIn && <ClaimFreeTickets className={paddingNonBlendY} release={featuredRelease} />}

      {sweepstakes.length > 0 && <Sweepstakes sweepstakes={sweepstakes} />}

      <PastReleases className={paddingNonBlendY} releases={pastReleases} />
    </main>
  )
}
